import React from 'react'
import PropTypes from 'prop-types'
import DateFnsUtils from '@date-io/date-fns'
import {
	MuiPickersUtilsProvider,
	DatePicker,
	KeyboardDatePicker,
	Day,
} from '@material-ui/pickers'
import isSameDay from 'date-fns/isSameDay'
import isWithinInterval from 'date-fns/isWithinInterval'
import { omit } from 'timm'
import { createComponent } from 'react-fela'
import { ThemeProvider as MuiThemeProvider } from '@material-ui/styles'
import { convertREDateFormatToStandardDate } from 'design-framework/utils/helpers'
import theme from 'design-framework/utils/base-ui-theme'
import { bmsTheme, __wrapper, __description } from './__style'

const Wrapper = createComponent(__wrapper, 'div', ['data-auto'])
const Description = createComponent(__description)

export default class DatePickerMaterial extends React.Component {
	constructor(props) {
		super(props)
		const { initialValue, shouldParseDate } = this.props
		this.state = {
			start: null,
			end: null,
			selectedDate: shouldParseDate
				? convertREDateFormatToStandardDate(initialValue)
				: initialValue || null,
		}
	}

	static getDerivedStateFromProps(props, state) {
		if (props.renderValueByProp) {
			if (
				Date.parse(new Date(props.initialValue)) !==
				Date.parse(new Date(state.selectedDate))
			) {
				return {
					selectedDate: props.initialValue || null,
				}
			}

			if (
				Date.parse(new Date(props.startDate)) !==
				Date.parse(new Date(state.start))
			) {
				return {
					start: props.startDate || null,
				}
			}

			if (
				Date.parse(new Date(props.endDate)) !==
				Date.parse(new Date(state.end))
			) {
				return {
					end: props.endDate || null,
				}
			}
		}

		return null
	}

	handleDateChange = newValue => {
		const { isDateRangePicker } = this.props
		if (!isDateRangePicker) {
			this.setState({ selectedDate: newValue })

			return
		}
		const { start, end } = this.state
		if (start && end) {
			this.setState({
				start: newValue,
				end: null,
			})
		} else if (!start) {
			this.setState({
				start: newValue,
			})
		} else if (new Date(newValue).getTime() > new Date(start).getTime()) {
			this.setState({
				end: newValue,
			})
		} else {
			this.setState({
				start: newValue,
			})
		}
	}

	renderCustomPickerDay = (
		date,
		selectedDate,
		isInCurrentMonth,
		dayComponent
	) => {
		const { isDateRangePicker, themeToUse = theme } = this.props
		// If it is not date range picker, no modification in day is required
		if (!isDateRangePicker) {
			return dayComponent
		}

		let style = {}
		const { end, start } = this.state
		const dayOnclick = () => {
			this.handleDateChange(date)
		}
		if (!end) {
			return <Day onClick={dayOnclick} {...dayComponent.props} />
		}
		const dayIsBetween = isWithinInterval(date, { start, end })

		const isFirstDay = isSameDay(date, start)
		const isLastDay = isSameDay(date, end)

		if (isFirstDay || isLastDay) {
			style = {
				background: themeToUse.primary(themeToUse).backgroundColor,
				color: '#FFF',
			}
		} else if (dayIsBetween) {
			style = {
				background: `${
					themeToUse.primary(themeToUse).backgroundColor
				}99`,
			}
		}

		return (
			<Day onClick={dayOnclick} {...dayComponent.props} style={style} />
		)
	}

	resetDates = () => {
		const { onClose } = this.props
		this.setState({
			start: null,
			end: null,
		})
		onClose()
	}

	handleDismiss = () => {
		const { onDismiss } = this.props
		this.setState({
			start: null,
			end: null,
		})
		onDismiss()
	}

	dateSelected = date => {
		const { isDateRangePicker } = this.props
		const { name, onDateChange = () => {} } = this.props
		const { start, end } = this.state
		this.setState({ selectedDate: date })
		if (!isDateRangePicker) {
			onDateChange(date, name)
		} else {
			onDateChange(start, end, name)
		}
	}

	shouldDisableThisDate = date => {
		const { disabledDates } = this.props

		return disabledDates[new Date(date).toLocaleDateString()]
	}

	render() {
		// omit these props to DatePicker as it does not recognize them
		const newProps = omit(this.props, [
			'onDateChange',
			'name',
			'floatingLabelText',
			'label',
			'initialValue',
			'renderKeyboardDatePicker',
			'style',
			'shouldParseDate',
			'renderValueByProp',
			'isDateRangePicker',
			'disabledDates',
			'onDismiss',
		])

		const {
			name,
			label,
			description,
			floatingLabelText,
			renderKeyboardDatePicker,
			style,
			inputElement,
			onDismiss,
			themeToUse = theme,
		} = this.props
		const { selectedDate } = this.state

		if (onDismiss) {
			newProps.onDismiss = this.handleDismiss
		}

		return (
			<Wrapper data-auto={`field-${name}`} style={style}>
				<MuiThemeProvider theme={bmsTheme(themeToUse)}>
					<MuiPickersUtilsProvider utils={DateFnsUtils}>
						{renderKeyboardDatePicker ? (
							<KeyboardDatePicker
								{...newProps}
								label={label || floatingLabelText}
								onChange={this.dateSelected}
								renderDay={this.renderCustomPickerDay}
								value={selectedDate}
								onClose={this.resetDates}
								TextFieldComponent={inputElement || undefined}
								fullWidth
								shouldDisableDate={this.shouldDisableThisDate}
							/>
						) : (
							<DatePicker
								{...newProps}
								label={label || floatingLabelText}
								onChange={this.dateSelected}
								renderDay={this.renderCustomPickerDay}
								value={selectedDate}
								onClose={this.resetDates}
								TextFieldComponent={inputElement || undefined}
								fullWidth
								shouldDisableDate={this.shouldDisableThisDate}
							/>
						)}
					</MuiPickersUtilsProvider>
				</MuiThemeProvider>
				{description && <Description>{description}</Description>}
			</Wrapper>
		)
	}
}

DatePickerMaterial.propTypes = {
	/**
	 * Disables the DatePicker.
	 */
	disabled: PropTypes.bool,
	/**
	 * The ending of a range of valid dates. The range includes the endDate.
	 * The default value is current date + 100 years.
	 */
	maxDate: PropTypes.string,
	/**
	 * The beginning of a range of valid dates. The range includes the startDate.
	 * The default value is current date - 100 years.
	 */
	minDate: PropTypes.string,
	/**
	 * Callback function that is fired when the date value changes.
	 *
	 * @param {null} null Since there is no particular event associated with the change,
	 * the first argument will always be null.
	 * @param {object} date The new date.
	 */
	onChange: PropTypes.func,
	/*
	 * Sets the date for the Date Picker programmatically.
	 */
	value: PropTypes.object,
	/**
	 * Call back to whoever uses this component
	 */
	onDateChange: PropTypes.func,
	/**
	 * Name to identify it in decide form components
	 */
	name: PropTypes.string,
	/**
	 * Initial date to be displayed
	 */
	initialValue: PropTypes.string,
	/**
	 * Parse initial date for the format
	 */
	shouldParseDate: PropTypes.bool,
	/**
	 * Render value by prop
	 */
	renderValueByProp: PropTypes.bool,
	/**
	 * If it is date range picker, then the value will be true.
	 */
	isDateRangePicker: PropTypes.bool,
	/**
	 * If you want to fire date picker from different html element / from your componet,
	 * just pass a callback function that should return your element. Don't forget to add
	 * onClick event listener which will be sent to your callback method as props.
	 * (Also sending value, label and onChange event handler, use them if requires)
	 */
	inputElement: PropTypes.func,
	/**
	 * Used to open / close datepicker programmatically
	 */
	open: PropTypes.bool,
	/**
	 * Callback function that is fired when the datepicker opens.
	 */
	onOpen: PropTypes.func,
	/**
	 * Callback function that is fired when the datepicker closes.
	 */
	onClose: PropTypes.func,
	/**
	 * Disables dates mentioned in the object, pass dates in toLocaleDateString format
	 */
	disabledDates: PropTypes.object,
	/** pass startDate and endDate with renderValueByProp to control values from outside */
	startDate: PropTypes.object,
	endDate: PropTypes.object,
}

DatePickerMaterial.defaultProps = {
	disabled: false,
	shouldParseDate: true,
	renderValueByProp: false,
	isDateRangePicker: false,
	disabledDates: {},
	onClose: () => {},
}
